import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Agent, User } from "@/types";

// #223 UC-5: Monats-Budget-Limit setzen (admin-only). null = Plattform-Default greift.
function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

export function useSetAgentBudget(agentId: string): UseMutationResult<Agent, Error, number | null> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (monthlyLimitUsd) =>
      getClient().patch<Agent>(`/agents/${agentId}/budget-policy`, { monthlyLimitUsd }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
      qc.invalidateQueries({ queryKey: queryKeys.subResource("agents", agentId, "budget") });
    },
  });
}

// #223 UC-A: Per-Turn-Budget-Cap setzen (admin-only). null = Plattform-Default, 0 = unbegrenzt.
// Greift live ab dem nächsten Turn (kein Relaunch).
export function useSetAgentTurnBudget(agentId: string): UseMutationResult<Agent, Error, number | null> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (limitUsd) =>
      getClient().patch<Agent>(`/agents/${agentId}/turn-budget`, { limitUsd }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
    },
  });
}

export function useSetUserBudget(userId: string): UseMutationResult<User, Error, number | null> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (monthlyLimitUsd) =>
      getClient().patch<User>(`/users/${userId}/budget-policy`, { monthlyLimitUsd }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("users") });
      qc.invalidateQueries({ queryKey: queryKeys.subResource("users", userId, "budget") });
    },
  });
}
